import Input from '../common/Input';
import '../../assets/css/common/input.css';
import Image from '../common/Image';
import logo from '../../assets/images/logo.svg';
import Label from '../common/Label';
import Button from '../common/Button';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import env from '../../configs/env';
import { resetStorage } from '../../redux/reducers/storage';
import { useDispatch } from 'react-redux';
import {
    setAuthority,
    setChangeRequestType,
    setIsAllowValidate,
    setOrganization,
    setStoreCode as setStoreCodeRedux,
} from '../../redux/actions/storage';
import { AdminRequest, AuthControllerService, ChangeRequestControllerService, PriceSearchRequest } from '../../services';
import { clearErrors } from '../../redux/actions/error';
import { setLoader } from '../../redux/actions/api';
import Loader from '../common/Loader';
import { setToastsError } from '../../redux/actions/common';
import { ToastContainer } from 'react-toastify';
import Toast from '../common/Toast';

// 画面種別
const SCREEN_TYPE = {
    // 店舗番号入力画面
    STORE_CODE: 1,

    // 認証コード入力画面
    AUTH_CODE: 2,
};

const Login = () => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    // 画面種別
    const [screenType, setScreenType] = useState(SCREEN_TYPE.STORE_CODE);

    // 店舗番号
    const [storeCode, setStoreCode] = useState('');

    // 認証コード
    const [authCode, setAuthCode] = useState('');

    /**
     * ログイン処理を行う
     *
     * @param params リクエストパラメーター一覧
     */
    const login = async (params) => {
        try {
            dispatch(setLoader(true));

            // ログインAPI
            const response = await axios.post(
                `${env.BACKEND_BASE_URL}/login`,
                {
                    username: params.storeCode,
                    password: params.authCode ?? '',
                },
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    withCredentials: true,
                },
            );

            const { storeCode, authority, organization } = response.data;
            const isAllowValidate = await ChangeRequestControllerService.getIsAllowChangeRequestWithoutValidation();

            dispatch(setLoader(false));
            dispatch(setStoreCodeRedux(storeCode));
            // デフォルトとして競合対抗をセット
            dispatch(setChangeRequestType(PriceSearchRequest.changeRequestType.COMPETITOR));
            // レスポンスをセット
            dispatch(setAuthority(authority));
            dispatch(setOrganization(organization));
            dispatch(setIsAllowValidate(isAllowValidate));

            switch (organization) {
                case AdminRequest.organization.STORE:
                    history('/select-menu');
                    break;
                case AdminRequest.organization.HEADQUARTERS:
                case AdminRequest.organization.GROCERY_HEADQUARTERS:
                    dispatch(setChangeRequestType(PriceSearchRequest.changeRequestType.FLYER));
                    history('/competitor-master');
                    break;
                case AdminRequest.organization.IT_HEADQUARTERS:
                    history('/users');
                    break;
                default:
            }
        } catch (error) {
            dispatch(setLoader(false));
            switch (error.response.status) {
                case 401:
                    // 認証コード無しの場合
                    if (params.authCode === undefined) {
                        dispatch(setToastsError('店舗番号を確認してください。'));
                    }
                    // 認証コード有りの場合
                    else {
                        dispatch(setToastsError('認証コードを確認してください。'));
                    }
                    break;
                default:
                    dispatch(setToastsError('通信が失敗しました。しばらく時間をおいて実行してください。'));
                    break;
            }
        }
    };

    /**
     * 「店舗番号入力画面」の「ログイン」ボタンをクリックした
     */
    const handleStoreCodeLoginClick = async () => {
        dispatch(clearErrors());

        // 本部一覧
        const headquarters = [
            '0000', // 店舗運営本部
            '9240', // グロサリー本部
            '9999', // IT本部
        ];

        // 本部 or 店舗の Editor（店舗番号 + B）の場合
        const isHeadquarter = headquarters.includes(storeCode);
        const isStoreEditor = !headquarters.includes(storeCode.slice(0, 4)) && storeCode.match(/^\d{4}B$/);
        if (isHeadquarter || isStoreEditor) {
            // 認証コード無しでログイン
            login({
                storeCode,
                // authCode,
            });
        }
        // それ以外（つまり店舗の Manager の場合）
        else {
            try {
                dispatch(setLoader(true));

                // 認証コード発行API
                await AuthControllerService.generateAuthCode({
                    storeCode,
                });

                dispatch(setLoader(false));

                // 認証コード入力画面に遷移
                setScreenType(SCREEN_TYPE.AUTH_CODE);
            } catch (error) {
                dispatch(setLoader(false));
                switch (error.status) {
                    case 400:
                        dispatch(setToastsError('店舗番号を確認してください。'));
                        break;
                    default:
                        dispatch(setToastsError('通信が失敗しました。しばらく時間をおいて実行してください。'));
                        break;
                }
            }
        }
    };

    /**
     * 「認証コード入力画面」の「戻る」ボタンをクリックした
     */
    const handlePasswordBackClick = () => {
        // 認証コードをクリア
        setAuthCode('');

        // 店舗番号入力画面に戻る
        setScreenType(SCREEN_TYPE.STORE_CODE);
    };

    /**
     * 「認証コード入力画面」の「ログイン」ボタンをクリックした
     */
    const handlePasswordLoginClick = async () => {
        dispatch(clearErrors());

        // ログイン
        login({
            storeCode,
            authCode,
        });
    };

    useEffect(() => {
        resetStorage();
        dispatch(clearErrors());
    }, [dispatch, location]);

    return (
        <>
            <Loader />
            <Toast />
            <ToastContainer />
            <div className="row no-gutters">
                <div className="col-12 col-md-6 col-lg-4 m-auto">
                    <div className="auth">
                        <div className="auth__inner">
                            <div className="auth__logo fade-in">
                                <Image src={logo} />
                            </div>
                            <div className="auth__form c-slide-top">
                                <div className="form-group">
                                    <Label text="店舗番号（数字4桁）" isRequired={true} />
                                    <Input
                                        name="storeCode"
                                        value={storeCode}
                                        type="text"
                                        placeholder="例：1001"
                                        readOnly={screenType !== SCREEN_TYPE.STORE_CODE}
                                        onChange={(value) => setStoreCode(value)}
                                    />
                                </div>
                                {screenType === SCREEN_TYPE.AUTH_CODE && (
                                    <div className="form-group">
                                        <Label text="認証コード" isRequired={true} />
                                        <Input
                                            name="authCode"
                                            value={authCode}
                                            type="text"
                                            placeholder="メールに記載の6桁の数字"
                                            onChange={(value) => setAuthCode(value)}
                                            data-rum-ignore={true} // CloudWatch RUM 操作ログには記録しない
                                        />
                                        <div className="mt-1 text-muted">
                                            店舗・店長宛に認証コードをメールで送信しました。
                                            <br />
                                            認証コード欄にコードを入力してください。
                                        </div>
                                    </div>
                                )}
                                <div className="auth__action">
                                    {screenType === SCREEN_TYPE.STORE_CODE ? (
                                        <Button
                                            type="button"
                                            className="btn btn--primary btn--lg"
                                            text="ログイン"
                                            onClick={handleStoreCodeLoginClick}
                                        />
                                    ) : (
                                        <>
                                            <Button
                                                type="button"
                                                className="btn btn--secondary btn--lg"
                                                text="戻る"
                                                onClick={handlePasswordBackClick}
                                            />
                                            <Button
                                                type="button"
                                                className="btn btn--primary btn--lg"
                                                text="ログイン"
                                                onClick={handlePasswordLoginClick}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
